import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  CompareGroupValueType,
  NewReadershipProfileStateInterface,
  NewReadershipProfileStepEnum,
  StudyObjectValueType,
} from '../../interfaces/analytics-details/new-readership-profile-state.interface'
import {
  CompareGroupType,
  StudyObjectGroupCompareEnum,
  StudyObjectType,
} from '../../../components/analytics-details-v2/view/readership-profile/configs/StudiesObjectsCompareGroupTypesConfig'
import {
  newAnalyticsPrivateReadershipProfileStatePostPreviewForReadershipProfile,
  newAnalyticsReadershipProfileStatePostPreviewForReadershipProfile,
  newAnalyticsReadershipProfileStatePutAnalyticsByIdValidate,
} from '../../actions/analytics-details/new-analytics-readership-profile.actions'
import { AnalyticDetailsInterface } from '../../../core/interface/analytics/analytic-details.interface'
import { AnalyticInputInterface } from '../../../core/interface/analytics/analytic-input.interface'
import { AnalyticInputTypeEnum } from '../../../core/enums/analytic-input-type.enum'
import { AnalyticOutputTypeEnum } from '../../../core/enums/analytic-output-type.enum'
import { AnalyticPreviewTypeErrorEnum } from '../../../core/enums/analytic-preview-type-error'
import { BookInterface } from '../../../core/interface/book.interface'
import {
  FILTER_KEY_AUTHORS,
  FILTER_KEY_EDITOR,
  FILTER_KEY_FORMAT,
  FILTER_KEY_PUBLISHING_DATE,
} from '../../../core/config/dynamics/filters-items-dynamics.config'
import { DateFilterTypeEnum } from '../../../core/enums/date-filter-type.enum'

const initialState: NewReadershipProfileStateInterface = {
  analyticsName: '',
  step: NewReadershipProfileStepEnum.START,
  studyObjectType: StudyObjectGroupCompareEnum.TITLE,
  studyObjectValue: null,
  studyObjectBooks: [],
  compareGroupType: StudyObjectGroupCompareEnum.TITLE,
  compareGroupValue: null,
  compareGroupBooks: [],
  isLoadingPostPreview: false,
  postPreviewSuccess: false,
  postPreviewError: false,
  previewId: null,
  previewOutput: null,
  previewInputs: null,
  getPreviewByIdSuccess: false,
  getPreviewByIdError: false,
  isLoadingGetPreviewById: false,
  isLoadingReadershipProfileValidation: false,
  analyticsReadershipProfilePreview: null,
  postReadershipProfileValidateSuccess: false,
  postReadershipProfileValidateError: false,
  postPreviewTypeError: null,
  studyObjectFiltersActions: {
    authors: [],
    edition: [],
    format: [],
    publishing_date_type: DateFilterTypeEnum.BETWEEN,
    publishing_date: [null, null],
  },
  compareGroupFiltersActions: {
    authors: [],
    edition: [],
    format: [],
    publishing_date_type: DateFilterTypeEnum.BETWEEN,
    publishing_date: [null, null],
  },
  studyObjectFiltersPayload: {
    type: 'studyObjectValue',
    added_in_library_first_date: null,
    added_in_library_last_date: null,
    min_books_ownd_in_input: null,
    tags: [],
  },
  compareGroupFiltersPayload: {
    type: 'compareGroupValue',
    added_in_library_first_date: null,
    added_in_library_last_date: null,
    min_books_ownd_in_input: null,
    tags: [],
  },
}

const newReadershipProfileSlice = createSlice({
  name: 'newReadershipProfileReducer',
  initialState,
  reducers: {
    newReadershipProfileStateSetAnalyticsName: (
      state: NewReadershipProfileStateInterface,
      action: PayloadAction<string>,
    ): NewReadershipProfileStateInterface => ({
      ...state,
      analyticsName: action.payload,
    }),
    newReadershipProfileStateSetStudyObjectType: (
      state: NewReadershipProfileStateInterface,
      action: PayloadAction<StudyObjectType>,
    ): NewReadershipProfileStateInterface => ({
      ...state,
      studyObjectType: action.payload,
    }),
    newReadershipProfileStateSetStudyObjectValue: (
      state: NewReadershipProfileStateInterface,
      action: PayloadAction<StudyObjectValueType>,
    ): NewReadershipProfileStateInterface => {
      return {
        ...state,
        studyObjectValue: action.payload,
      }
    },

    newReadershipProfileStateSetStudyObjectBooks: (
      state: NewReadershipProfileStateInterface,
      action: PayloadAction<BookInterface[]>,
    ): NewReadershipProfileStateInterface => ({
      ...state,
      studyObjectBooks: action.payload,
    }),
    newReadershipProfileStateSetCompareGroupType: (
      state: NewReadershipProfileStateInterface,
      action: PayloadAction<CompareGroupType>,
    ): NewReadershipProfileStateInterface => ({
      ...state,
      compareGroupType: action.payload,
    }),
    newReadershipProfileStateSetCompareGroupValue: (
      state: NewReadershipProfileStateInterface,
      action: PayloadAction<CompareGroupValueType>,
    ): NewReadershipProfileStateInterface => {
      return {
        ...state,
        compareGroupValue: action.payload,
      }
    },
    newReadershipProfileStateSetCompareGroupBooks: (
      state: NewReadershipProfileStateInterface,
      action: PayloadAction<BookInterface[]>,
    ): NewReadershipProfileStateInterface => ({
      ...state,
      compareGroupBooks: action.payload,
    }),
    newReadershipProfileStateReinitializePostPreview: (
      state: NewReadershipProfileStateInterface,
      action: PayloadAction<number[]>,
    ): NewReadershipProfileStateInterface => ({
      ...state,
      postPreviewSuccess: false,
      postPreviewError: false,
    }),
    newReadershipProfileStateGotToStartStep: (
      state: NewReadershipProfileStateInterface,
    ): NewReadershipProfileStateInterface => ({
      ...state,
      step: NewReadershipProfileStepEnum.START,
    }),
    reinitializeNewReadershipProfileState: (
      state: NewReadershipProfileStateInterface,
    ): NewReadershipProfileStateInterface => ({
      ...initialState,
    }),
    newReadershipProfileResetStudyObjectFilter: (
      state: NewReadershipProfileStateInterface,
    ): NewReadershipProfileStateInterface => ({
      ...state,
      studyObjectFiltersActions: initialState.studyObjectFiltersActions,
    }),
    newReadershipProfileResetCompareGroupFilter: (
      state: NewReadershipProfileStateInterface,
    ): NewReadershipProfileStateInterface => ({
      ...state,
      compareGroupFiltersActions: initialState.compareGroupFiltersActions,
    }),
    newReadershipProfileResetStudyObjectUserFilters: (
      state: NewReadershipProfileStateInterface,
    ): NewReadershipProfileStateInterface => ({
      ...state,
      studyObjectFiltersPayload: initialState.studyObjectFiltersPayload,
    }),
    newReadershipProfileResetCompareGroupUserFilters: (
      state: NewReadershipProfileStateInterface,
    ): NewReadershipProfileStateInterface => ({
      ...state,
      compareGroupFiltersPayload: initialState.compareGroupFiltersPayload,
    }),
    newReadershipProfileStateSetPostPreviewTypeError: (
      state: NewReadershipProfileStateInterface,
      action: PayloadAction<AnalyticPreviewTypeErrorEnum>,
    ): NewReadershipProfileStateInterface => ({
      ...state,
      postPreviewTypeError: action.payload,
    }),
    newReadershipProfileStateGoToDetailsStep: (
      state: NewReadershipProfileStateInterface,
    ): NewReadershipProfileStateInterface => ({
      ...state,
      step: NewReadershipProfileStepEnum.DETAILS,
    }),
    setAnalyticsStudyObjectFiltersActions: (
      state: NewReadershipProfileStateInterface,
      action: PayloadAction<any>,
    ): NewReadershipProfileStateInterface => ({
      ...state,
      studyObjectFiltersActions: action.payload,
    }),

    setAnalyticsCompareGroupFiltersActions: (
      state: NewReadershipProfileStateInterface,
      action: PayloadAction<any>,
    ): NewReadershipProfileStateInterface => ({
      ...state,
      compareGroupFiltersActions: action.payload,
    }),

    setAnalyticsStudyObjectFiltersPayload: (
      state: NewReadershipProfileStateInterface,
      action: PayloadAction<any>,
    ): NewReadershipProfileStateInterface => ({
      ...state,
      studyObjectFiltersPayload: action.payload,
    }),

    setAnalyticsCompareGroupFiltersPayload: (
      state: NewReadershipProfileStateInterface,
      action: PayloadAction<any>,
    ): NewReadershipProfileStateInterface => ({
      ...state,
      compareGroupFiltersPayload: action.payload,
    }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        newAnalyticsReadershipProfileStatePostPreviewForReadershipProfile.pending,
        (
          state: NewReadershipProfileStateInterface,
        ): NewReadershipProfileStateInterface => {
          return {
            ...state,
            isLoadingPostPreview: true,
            postPreviewSuccess: false,
            postPreviewError: false,
            previewOutput: null,
            step: NewReadershipProfileStepEnum.PREVIEW,
            analyticsReadershipProfilePreview: null,
          }
        },
      )
      .addCase(
        newAnalyticsReadershipProfileStatePostPreviewForReadershipProfile.fulfilled,
        (
          state: NewReadershipProfileStateInterface,
          action: PayloadAction<AnalyticDetailsInterface>,
        ): NewReadershipProfileStateInterface => ({
          ...state,
          isLoadingPostPreview: false,
          postPreviewSuccess: true,
          postPreviewError: false,
          step: NewReadershipProfileStepEnum.PREVIEW,
          previewOutput:
            action.payload.inputs
              .find(
                (input: AnalyticInputInterface) =>
                  input.type === AnalyticInputTypeEnum.READERSHIP_PROFILE,
              )
              ?.outputs.find(
                (output) =>
                  output.type ===
                  AnalyticOutputTypeEnum.READERSHIP_PROFILE_PREVIEW,
              )?.readership_profile_preview || null,
          analyticsReadershipProfilePreview: action.payload,
        }),
      )
      .addCase(
        newAnalyticsReadershipProfileStatePostPreviewForReadershipProfile.rejected,
        (
          state: NewReadershipProfileStateInterface,
        ): NewReadershipProfileStateInterface => ({
          ...state,
          isLoadingPostPreview: false,
          postPreviewSuccess: false,
          postPreviewError: true,
          analyticsReadershipProfilePreview: null,
        }),
      )
      .addCase(
        newAnalyticsPrivateReadershipProfileStatePostPreviewForReadershipProfile.pending,
        (
          state: NewReadershipProfileStateInterface,
        ): NewReadershipProfileStateInterface => {
          return {
            ...state,
            isLoadingPostPreview: true,
            postPreviewSuccess: false,
            postPreviewError: false,
            previewOutput: null,
            step: NewReadershipProfileStepEnum.PREVIEW,
            analyticsReadershipProfilePreview: null,
          }
        },
      )
      .addCase(
        newAnalyticsPrivateReadershipProfileStatePostPreviewForReadershipProfile.fulfilled,
        (
          state: NewReadershipProfileStateInterface,
          action: PayloadAction<AnalyticDetailsInterface>,
        ): NewReadershipProfileStateInterface => {
          const privateInput = action.payload.inputs.find(
            (input: AnalyticInputInterface) =>
              input.type === AnalyticInputTypeEnum.PRIVATE_READERSHIP_PROFILE,
          )

          const previewOutputValue =
            privateInput?.outputs.find(
              (output) =>
                output.type ===
                AnalyticOutputTypeEnum.READERSHIP_PROFILE_PREVIEW,
            )?.readership_profile_preview ?? null

          return {
            ...state,
            isLoadingPostPreview: false,
            postPreviewSuccess: true,
            postPreviewError: false,
            step: NewReadershipProfileStepEnum.PREVIEW,
            previewOutput: previewOutputValue,
            analyticsReadershipProfilePreview: action.payload,
            previewInputs: privateInput
              ? {
                  private_readership_book_input:
                    privateInput.private_readership_book_input || [],
                  private_readership_users_input:
                    privateInput.private_readership_users_input || [],
                }
              : null,
          }
        },
      )
      .addCase(
        newAnalyticsPrivateReadershipProfileStatePostPreviewForReadershipProfile.rejected,
        (
          state: NewReadershipProfileStateInterface,
        ): NewReadershipProfileStateInterface => ({
          ...state,
          isLoadingPostPreview: false,
          postPreviewSuccess: false,
          postPreviewError: true,
          analyticsReadershipProfilePreview: null,
        }),
      )
      .addCase(
        newAnalyticsReadershipProfileStatePutAnalyticsByIdValidate.pending,
        (
          state: NewReadershipProfileStateInterface,
        ): NewReadershipProfileStateInterface => ({
          ...state,
          isLoadingReadershipProfileValidation: true,
          postReadershipProfileValidateSuccess: false,
          postReadershipProfileValidateError: false,
        }),
      )
      .addCase(
        newAnalyticsReadershipProfileStatePutAnalyticsByIdValidate.fulfilled,
        (
          state: NewReadershipProfileStateInterface,
        ): NewReadershipProfileStateInterface => ({
          ...state,
          isLoadingReadershipProfileValidation: false,
          postReadershipProfileValidateSuccess: true,
          postReadershipProfileValidateError: false,
        }),
      )
      .addCase(
        newAnalyticsReadershipProfileStatePutAnalyticsByIdValidate.rejected,
        (
          state: NewReadershipProfileStateInterface,
        ): NewReadershipProfileStateInterface => ({
          ...state,
          isLoadingReadershipProfileValidation: false,
          postReadershipProfileValidateSuccess: false,
          postReadershipProfileValidateError: true,
        }),
      )
  },
})

export const {
  reinitializeNewReadershipProfileState,
  newReadershipProfileResetStudyObjectFilter,
  newReadershipProfileResetCompareGroupFilter,
  newReadershipProfileResetStudyObjectUserFilters,
  newReadershipProfileResetCompareGroupUserFilters,
  newReadershipProfileStateSetAnalyticsName,
  newReadershipProfileStateReinitializePostPreview,
  newReadershipProfileStateSetStudyObjectType,
  newReadershipProfileStateSetCompareGroupType,
  newReadershipProfileStateSetStudyObjectBooks,
  newReadershipProfileStateSetStudyObjectValue,
  newReadershipProfileStateSetCompareGroupValue,
  newReadershipProfileStateSetCompareGroupBooks,
  newReadershipProfileStateGotToStartStep,
  newReadershipProfileStateSetPostPreviewTypeError,
  newReadershipProfileStateGoToDetailsStep,
  setAnalyticsStudyObjectFiltersActions,
  setAnalyticsCompareGroupFiltersActions,
  setAnalyticsStudyObjectFiltersPayload,
  setAnalyticsCompareGroupFiltersPayload,
} = newReadershipProfileSlice.actions
export const newReadershipProfileReducer = newReadershipProfileSlice.reducer
